import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { post } from '../../../api/client';

/**
 * 激活账户页（/activate-account?token=xxx）
 *
 * 注册后通过邮件中的激活链接进入此页面。
 * 提交 POST /auth/activate-account，成功后跳转到登录页。
 */
export default function ActivateAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('缺少激活令牌，请通过邮件中的激活链接进入此页面');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await post('/auth/activate-account', { token });
        if (!cancelled) {
          setStatus('success');
          setMessage('邮箱验证成功！');
        }
      } catch (e) {
        if (!cancelled) {
          setStatus('error');
          setMessage(e instanceof Error ? e.message : '激活失败，链接可能已过期');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
      <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-xl px-8 py-10 text-center">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">验证中...</h3>
            <p className="text-sm text-slate-500">正在验证您的邮箱，请稍候</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-teal-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">邮箱验证成功</h3>
            <p className="text-sm text-slate-500 mb-6">您现在可以使用所有系统功能</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              前往登录
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">验证失败</h3>
            <p className="text-sm text-slate-500 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors"
            >
              返回登录
            </button>
          </>
        )}
      </div>
    </div>
  );
}
